import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Network, ExternalLink, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const W = 900, H = 680;

// Type metadata: color, human label and hierarchical layer (0 = top).
const TIPO_META = {
  Manuale: { color: "#7c3aed", label: "Manuale", layer: 0 },
  PG: { color: "#1D4ED8", label: "Procedura (PG)", layer: 1 },
  IO: { color: "#0369a1", label: "Istruzione (IO)", layer: 2 },
  MOD: { color: "#b45309", label: "Modulo (MOD)", layer: 3 },
  altro: { color: "#475569", label: "Altro", layer: 4 },
};
const metaOf = (t) => TIPO_META[t] || TIPO_META.altro;
// Compact label shown on a node: first segment before " - ", truncated.
const shortCode = (c) => {
  let s = (c || "").split(" - ")[0].trim();
  if (s.length > 11) s = s.slice(0, 10) + "…";
  return s;
};

export default function Mappa() {
  const { user } = useAuth();
  const { tenantId, currentOrg } = useTenant();
  const navigate = useNavigate();
  const isConsultant = user?.role === "consultant";
  const [data, setData] = useState({ nodes: [], edges: [] });
  const [selected, setSelected] = useState(null);

  const needTenant = isConsultant && !tenantId;

  useEffect(() => {
    if (needTenant) { setData({ nodes: [], edges: [] }); return; }
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/graph${p}`).then((r) => { setData(r.data); setSelected(null); }).catch(() => {});
  }, [tenantId, needTenant]);

  // Directed dependents: edge source->target means "source depends on target",
  // so modifying `target` impacts `source`. dependents[target] = {sources}.
  const dependents = useMemo(() => {
    const dep = {};
    data.nodes.forEach((n) => (dep[n.id] = new Set()));
    data.edges.forEach((e) => { dep[e.target]?.add(e.source); });
    return dep;
  }, [data]);

  // Node importance = incoming dependencies (how many docs rely on it).
  const inDegree = useMemo(() => {
    const m = {};
    data.nodes.forEach((n) => (m[n.id] = (dependents[n.id]?.size) || 0));
    return m;
  }, [data, dependents]);

  // Impact chain follows the dependency direction (downstream dependents).
  const impacted = useMemo(() => {
    if (!selected) return new Set();
    const seen = new Set([selected]);
    const queue = [selected];
    while (queue.length) {
      const cur = queue.shift();
      (dependents[cur] || []).forEach((nb) => { if (!seen.has(nb)) { seen.add(nb); queue.push(nb); } });
    }
    return seen;
  }, [selected, dependents]);

  const nodeById = (id) => data.nodes.find((n) => n.id === id);
  const impactedList = [...impacted].filter((id) => id !== selected).map(nodeById).filter(Boolean);
  const baseR = data.nodes.length > 28 ? 11 : data.nodes.length > 16 ? 14 : 17;
  const radiusOf = (id) => baseR + Math.min((inDegree[id] || 0) * 3, 14);
  const fs = data.nodes.length > 28 ? 8 : 10;

  // Hierarchical tree layout: layers by document type, wrapped into multiple
  // rows per layer so dense clients (many docs) never overlap.
  const LAYOUT = useMemo(() => {
    const nodes = data.nodes;
    if (!nodes.length) return { pos: {}, bands: [], height: 360 };
    const byLayer = new Map();
    nodes.forEach((nd) => {
      const L = metaOf(nd.tipo).layer;
      if (!byLayer.has(L)) byLayer.set(L, []);
      byLayer.get(L).push(nd);
    });
    const layers = [...byLayer.keys()].sort((a, b) => a - b);
    const padX = 70, padTop = 46, padBottom = 30;
    const usableW = W - 2 * padX;
    const n = nodes.length;
    const spacingX = n > 44 ? 98 : n > 28 ? 112 : n > 16 ? 128 : 150;
    const cols = Math.max(1, Math.floor(usableW / spacingX));
    const rowH = n > 40 ? 58 : 66;
    const headerH = 30, layerGap = 30;
    const pos = {}, bands = [];
    let y = padTop;
    layers.forEach((L) => {
      const members = byLayer.get(L).slice().sort((a, b) => (a.codice || "").localeCompare(b.codice || ""));
      const m = members.length;
      const rows = Math.ceil(m / cols);
      const meta = Object.values(TIPO_META).find((t) => t.layer === L) || TIPO_META.altro;
      bands.push({ labelY: y, lineY: y + 8, label: meta.label, color: meta.color, count: m });
      const nodesTop = y + headerH;
      members.forEach((nd, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        pos[nd.id] = { x: padX + (c + 0.5) * (usableW / cols), y: nodesTop + r * rowH };
      });
      y = nodesTop + rows * rowH + layerGap;
    });
    return { pos, bands, height: Math.max(360, y + padBottom) };
  }, [data]);

  const positions = LAYOUT.pos;

  return (
    <div className="space-y-6">
      <PageHero label="Simulazione impatto" title="Mappa collegamenti"
        subtitle={<>Grafo dei collegamenti tra i documenti di {tenantId ? <strong>{currentOrg?.nome}</strong> : "un cliente"}. I nodi più grandi sono quelli da cui dipendono più documenti. Clicca un documento per evidenziare la catena d'impatto.</>} />

      {needTenant ? (
        <Card className="p-12 text-center border-dashed">
          <Network className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Seleziona un cliente dal menu in alto per visualizzare la mappa.</p>
        </Card>
      ) : data.nodes.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Network className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun documento da mappare per questo cliente.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200 p-2 overflow-hidden">
            <div className="flex items-center gap-2 px-2 pt-2 pb-1">
              <Network className="h-4 w-4 text-blue-700" />
              <span className="text-sm font-medium text-slate-700">Gerarchia documentale</span>
              <span className="text-xs text-slate-500">Manuale → Procedure → Istruzioni → Moduli</span>
            </div>
            <svg viewBox={`0 0 ${W} ${LAYOUT.height}`} className="w-full h-auto" data-testid="graph-svg"
              onClick={() => setSelected(null)}>
              <defs>
                <marker id="arrow" markerWidth="9" markerHeight="9" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#94a3b8" />
                </marker>
                <marker id="arrow-active" markerWidth="9" markerHeight="9" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#2563EB" />
                </marker>
              </defs>

              {LAYOUT.bands.map((b, i) => (
                <g key={"band" + i}>
                  <line x1={20} y1={b.lineY} x2={W - 20} y2={b.lineY} stroke={b.color} strokeOpacity="0.12" strokeWidth="1" strokeDasharray="4 4" />
                  <text x={20} y={b.labelY} fontSize="11" fontWeight="700" fill={b.color}>{`${b.label} · ${b.count}`}</text>
                </g>
              ))}

              {data.edges.map((e, i) => {
                const s = positions[e.source], t = positions[e.target];
                if (!s || !t) return null;
                // shorten to node border so the arrowhead is visible
                const tr = radiusOf(e.target) + 4;
                const dx = t.x - s.x, dy = t.y - s.y;
                const len = Math.hypot(dx, dy) || 1;
                const ex = t.x - (dx / len) * tr, ey = t.y - (dy / len) * tr;
                const active = selected && impacted.has(e.source) && impacted.has(e.target);
                return (
                  <line key={i} x1={s.x} y1={s.y} x2={ex} y2={ey}
                    stroke={active ? "#2563EB" : "#94a3b8"} strokeWidth={active ? 2.6 : 1.2}
                    markerEnd={active ? "url(#arrow-active)" : "url(#arrow)"}
                    opacity={selected ? (active ? 1 : 0.1) : 0.5} />
                );
              })}

              {data.nodes.map((node) => {
                const p = positions[node.id];
                if (!p) return null;
                const isSel = node.id === selected;
                const isImp = impacted.has(node.id);
                const dim = selected && !isImp;
                const r = radiusOf(node.id);
                const color = metaOf(node.tipo).color;
                return (
                  <g key={node.id} data-testid={`graph-node-${node.id}`} style={{ cursor: "pointer" }}
                    opacity={dim ? 0.25 : 1}
                    onClick={(ev) => { ev.stopPropagation(); setSelected(node.id); }}>
                    <title>{node.codice} — {node.titolo} · {inDegree[node.id] || 0} dipendenti</title>
                    {isSel && <circle cx={p.x} cy={p.y} r={r + 7} fill="none" stroke={color} strokeWidth="1.5" strokeOpacity="0.4" />}
                    <circle cx={p.x} cy={p.y} r={isSel ? r + 3 : r}
                      fill={isSel || isImp ? color : "#ffffff"} stroke={color} strokeWidth={isSel ? 3 : 2} />
                    <text x={p.x} y={p.y + fs / 3} textAnchor="middle" fontSize={fs} fontWeight="700"
                      fill={isSel || isImp ? "#ffffff" : color} style={{ pointerEvents: "none" }}>{shortCode(node.codice)}</text>
                  </g>
                );
              })}
            </svg>

            <div className="flex flex-wrap items-center gap-3 px-3 pb-2 pt-1 border-t border-slate-100">
              {Object.values(TIPO_META).map((m) => (
                <span key={m.label} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} /> {m.label}
                </span>
              ))}
              <span className="flex items-center gap-1 text-xs text-slate-500 ml-auto">
                <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
                <span className="inline-block h-3 w-3 rounded-full bg-slate-400" />
                dimensione = documenti dipendenti
              </span>
            </div>
          </Card>

          <Card className="border-slate-200 p-6">
            {selected ? (
              <>
                <div className="label-caps">Documento selezionato</div>
                <div className="mt-1 mb-1 font-display font-semibold">{nodeById(selected)?.codice}</div>
                <div className="text-sm text-slate-600">{nodeById(selected)?.titolo}</div>
                <div className="mt-2">
                  <Badge variant="outline" className="text-[11px]">{metaOf(nodeById(selected)?.tipo).label}</Badge>
                </div>
                <Button variant="outline" size="sm" className="mt-3"
                  onClick={() => navigate(`/documenti/${selected}`)} data-testid="open-doc-btn">
                  <ExternalLink className="h-4 w-4 mr-2" /> Apri documento
                </Button>
                <div className="mt-6">
                  <div className="label-caps mb-2">Documenti impattati ({impactedList.length})</div>
                  <p className="text-xs text-slate-500 mb-2">Documenti che dipendono da questo (a catena) e vanno rivisti se lo modifichi.</p>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="impacted-list">
                    {impactedList.length ? impactedList.map((d) => (
                      <button key={d.id} onClick={() => setSelected(d.id)}
                        className="w-full text-left flex items-center gap-2 p-2 rounded-md hover:bg-blue-50 transition-colors">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: metaOf(d.tipo).color }} />
                        <Badge variant="outline" className="font-mono text-[11px]">{d.codice}</Badge>
                        <span className="text-sm truncate">{d.titolo}</span>
                      </button>
                    )) : <p className="text-sm text-slate-500">Nessun documento dipende da questo: modificarlo non ha impatti a catena.</p>}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-10">
                <Info className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Clicca un nodo per simulare l'impatto di una sua modifica sui documenti collegati.</p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
